/**
 * Order-aware structural differ for IScreenDesign trees.
 *
 * `diffScreenDesign(prev, cur, makeOpId)` emits an op list such that applying it to
 * `prev` (via `applyCollabOps`) yields `cur` EXACTLY, including all child ordering.
 * Neither input is mutated. Collab ops are exact: GUID-case-only string differences
 * are NOT suppressed here (unlike the branching differ).
 *
 * Emission order (load-bearing — apply executes ops sequentially):
 *  1. Structural adds + moves, walking `cur` in document order (DFS). Walking in
 *     document order guarantees every `afterSiblingId` anchor was either a survivor
 *     or has already been placed by an earlier op, so anchor-relative insertion
 *     reproduces the exact target order.
 *  2. Removes. These come AFTER the moves on purpose: a survivor that escaped a
 *     removed subtree must be moved out before the subtree is deleted, otherwise
 *     the remove would take the survivor with it.
 *  3. Per-node property ops for surviving nodes.
 *  4. Root screen-field ops.
 *
 * Structural notes:
 *  - Node identity is `normalizeNodeId(id)`: numeric 5 and string "5" are the same
 *    node (never remove+add). The virtual parent of top-level items is "".
 *  - `nodeAdd` is emitted only for TOPMOST added roots; the payload is the deep-cloned
 *    `cur` subtree with surviving descendants PRUNED out — survivors are brought into
 *    the new subtree by their own `nodeMove` ops so a node never exists twice.
 *  - Same-parent reorders emit the minimal move set: the longest increasing
 *    subsequence (over prev positions) of the surviving same-parent siblings stays
 *    put; everything else moves.
 *  - `parentId` is derived state maintained by the applier; it is excluded from the
 *    property diff (along with `id` and `children`).
 */

import { IDisplayItem } from "../../models/display-item";
import { IScreenDesign } from "../../models/screen-design";
import {
  cloneJsonValue,
  CollabJsonValue,
  deepJsonEqual,
  ICollabJsonObject,
  isJsonArray,
  isJsonObject
} from "./sd-json";
import { ICollabOp } from "./sd-patch-types";
import { buildPointer, CollabPathSegment, normalizeNodeId } from "./sd-path";

interface INodeIndexEntry {
  node: IDisplayItem;
  parentIdNorm: string;
  index: number;
}

type NodeIndex = Map<string, INodeIndexEntry>;

type ArrayKeyKind = "id" | "name";

const EXCLUDED_NODE_KEYS: ReadonlySet<string> = new Set<string>(["children", "id", "parentId"]);
/**
 * Root keys the screen-field diff never emits ops for. `displayItems` is the node tree
 * (handled structurally) and `id` is the document identity.
 *
 * `publicMetaData` is the screen's own publicJson metadata, carried on the tree object only
 * as transport from the single-screen GET to the provider's side-channel (KOA-18173
 * follow-up). It belongs to the PublicJSON column, is stripped from every outbound
 * serialization, and must never travel as a collab op: the seed is built from the stripped
 * copy while the live doc still carries it, so without this exclusion the first local edit
 * would emit a bogus `screenPropSet /publicMetaData` and push metadata through the tree
 * channel — which has no LWW protection for it.
 */
const EXCLUDED_SCREEN_KEYS: ReadonlySet<string> = new Set<string>(["displayItems", "id", "publicMetaData"]);

function childrenOf(node: IDisplayItem): readonly IDisplayItem[] {
  const children: unknown = node.children;
  if (Array.isArray(children)) {
    return children;
  }
  return [];
}

function indexTree(displayItems: readonly IDisplayItem[]): NodeIndex {
  const index: NodeIndex = new Map<string, INodeIndexEntry>();
  const visit = (children: readonly IDisplayItem[], parentIdNorm: string): void => {
    for (let i = 0; i < children.length; i++) {
      const child: IDisplayItem = children[i];
      const idNorm: string = normalizeNodeId(child.id);
      if (idNorm !== "" && !index.has(idNorm)) {
        index.set(idNorm, { node: child, parentIdNorm, index: i });
      }
      visit(childrenOf(child), idNorm);
    }
  };
  visit(displayItems, "");
  return index;
}

/**
 * Positions (indices into `values`) of one longest strictly increasing subsequence.
 * Deterministic for a given input (patience algorithm with predecessor links).
 * Exported for reuse by the wf-patch differ (same minimal-move reorder strategy).
 */
export function longestIncreasingSubsequenceKeep(values: readonly number[]): Set<number> {
  const keep: Set<number> = new Set<number>();
  const n: number = values.length;
  if (n === 0) {
    return keep;
  }
  const tailIndices: number[] = [];
  const predecessors: number[] = new Array(n).fill(-1) as number[];
  for (let i = 0; i < n; i++) {
    let lo: number = 0;
    let hi: number = tailIndices.length;
    while (lo < hi) {
      const mid: number = (lo + hi) >> 1;
      if (values[tailIndices[mid]] < values[i]) {
        lo = mid + 1;
      } else {
        hi = mid;
      }
    }
    if (lo > 0) {
      predecessors[i] = tailIndices[lo - 1];
    }
    tailIndices[lo] = i;
  }
  let cursor: number = tailIndices[tailIndices.length - 1];
  while (cursor >= 0) {
    keep.add(cursor);
    cursor = predecessors[cursor];
  }
  return keep;
}

function hasDuplicates(values: readonly string[]): boolean {
  return new Set<string>(values).size !== values.length;
}

function stringArraysEqual(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
}

/** A key value is usable only when it produces an unambiguous, parseable selector. */
function elementKeyIsUsable(element: CollabJsonValue, keyKind: ArrayKeyKind): boolean {
  if (!isJsonObject(element)) {
    return false;
  }
  const keyValue: CollabJsonValue = element[keyKind];
  if (keyKind === "id") {
    if (typeof keyValue !== "string" && typeof keyValue !== "number") {
      return false;
    }
  } else if (typeof keyValue !== "string") {
    return false;
  }
  const canonical: string = keyKind === "id" ? normalizeNodeId(keyValue as string | number) : (keyValue as string);
  return canonical !== "" && !canonical.includes("/") && !canonical.includes("]");
}

function canonicalElementKey(element: CollabJsonValue, keyKind: ArrayKeyKind): string {
  const obj: ICollabJsonObject = element as ICollabJsonObject;
  if (keyKind === "id") {
    return normalizeNodeId(obj.id as string | number);
  }
  return obj.name as string;
}

function detectSharedArrayKey(
  prevArr: readonly CollabJsonValue[],
  curArr: readonly CollabJsonValue[]
): ArrayKeyKind | null {
  if (prevArr.length === 0 && curArr.length === 0) {
    return null;
  }
  const idUsable: boolean =
    prevArr.every((element: CollabJsonValue): boolean => elementKeyIsUsable(element, "id")) &&
    curArr.every((element: CollabJsonValue): boolean => elementKeyIsUsable(element, "id"));
  if (idUsable) {
    return "id";
  }
  const nameUsable: boolean =
    prevArr.every((element: CollabJsonValue): boolean => elementKeyIsUsable(element, "name")) &&
    curArr.every((element: CollabJsonValue): boolean => elementKeyIsUsable(element, "name"));
  if (nameUsable) {
    return "name";
  }
  return null;
}

export function diffScreenDesign(prev: IScreenDesign, cur: IScreenDesign, makeOpId: () => string): ICollabOp[] {
  const prevItems: readonly IDisplayItem[] = Array.isArray(prev.displayItems) ? prev.displayItems : [];
  const curItems: readonly IDisplayItem[] = Array.isArray(cur.displayItems) ? cur.displayItems : [];
  const prevIndex: NodeIndex = indexTree(prevItems);
  const curIndex: NodeIndex = indexTree(curItems);
  const ops: ICollabOp[] = [];

  function emitPropSet(nodeIdNorm: string, segments: readonly CollabPathSegment[], value: CollabJsonValue): void {
    ops.push({
      opId: makeOpId(),
      kind: "propSet",
      nodeId: nodeIdNorm,
      path: buildPointer(segments),
      value: cloneJsonValue(value)
    });
  }

  function emitPropRemove(nodeIdNorm: string, segments: readonly CollabPathSegment[]): void {
    ops.push({
      opId: makeOpId(),
      kind: "propRemove",
      nodeId: nodeIdNorm,
      path: buildPointer(segments)
    });
  }

  /**
   * Deep-clones an added subtree for a nodeAdd payload, PRUNING surviving
   * descendants: those stay in the tree and are relocated by their own nodeMove
   * ops (emitted by walkCurChildren in document order), so ids never duplicate.
   */
  function clonePrunedAddedSubtree(node: IDisplayItem): ICollabJsonObject {
    const source: ICollabJsonObject = node as unknown as ICollabJsonObject;
    const payload: ICollabJsonObject = {};
    for (const key of Object.keys(source)) {
      if (key === "children") {
        continue;
      }
      const clonedMember: CollabJsonValue = cloneJsonValue(source[key]);
      if (clonedMember !== undefined) {
        payload[key] = clonedMember;
      }
    }
    const prunedChildren: CollabJsonValue[] = [];
    for (const child of childrenOf(node)) {
      const childIdNorm: string = normalizeNodeId(child.id);
      if (prevIndex.has(childIdNorm)) {
        continue;
      }
      prunedChildren.push(clonePrunedAddedSubtree(child));
    }
    payload.children = prunedChildren;
    return payload;
  }

  /** Ids of the same-parent survivors that keep their relative order (LIS members). */
  function computeStableStayers(parentIdNorm: string, children: readonly IDisplayItem[]): Set<string> {
    const stayerIds: string[] = [];
    const stayerPrevIndices: number[] = [];
    for (const child of children) {
      const idNorm: string = normalizeNodeId(child.id);
      const prevEntry: INodeIndexEntry | undefined = prevIndex.get(idNorm);
      if (prevEntry !== undefined && prevEntry.parentIdNorm === parentIdNorm) {
        stayerIds.push(idNorm);
        stayerPrevIndices.push(prevEntry.index);
      }
    }
    const keepPositions: Set<number> = longestIncreasingSubsequenceKeep(stayerPrevIndices);
    const keep: Set<string> = new Set<string>();
    for (const position of keepPositions) {
      keep.add(stayerIds[position]);
    }
    return keep;
  }

  /**
   * Emits adds and moves for one cur sibling list, left to right. Inside an added
   * subtree the added children already travel in the payload (no op), while every
   * surviving child needs a nodeMove into its new home.
   */
  function walkCurChildren(parentIdNorm: string, children: readonly IDisplayItem[], insideAddedSubtree: boolean): void {
    const stableStayers: Set<string> = insideAddedSubtree
      ? new Set<string>()
      : computeStableStayers(parentIdNorm, children);
    let previousSiblingId: string | null = null;
    for (const child of children) {
      const idNorm: string = normalizeNodeId(child.id);
      if (idNorm === "") {
        continue;
      }
      const prevEntry: INodeIndexEntry | undefined = prevIndex.get(idNorm);
      if (prevEntry === undefined) {
        if (!insideAddedSubtree) {
          ops.push({
            opId: makeOpId(),
            kind: "nodeAdd",
            nodeId: idNorm,
            parentId: parentIdNorm,
            afterSiblingId: previousSiblingId,
            nodePayload: clonePrunedAddedSubtree(child)
          });
        }
        walkCurChildren(idNorm, childrenOf(child), true);
      } else {
        const needsMove: boolean =
          insideAddedSubtree || prevEntry.parentIdNorm !== parentIdNorm || !stableStayers.has(idNorm);
        if (needsMove) {
          ops.push({
            opId: makeOpId(),
            kind: "nodeMove",
            nodeId: idNorm,
            parentId: parentIdNorm,
            afterSiblingId: previousSiblingId
          });
        }
        walkCurChildren(idNorm, childrenOf(child), false);
      }
      previousSiblingId = idNorm;
    }
  }

  function diffValue(
    prevVal: CollabJsonValue,
    curVal: CollabJsonValue,
    nodeIdNorm: string,
    segments: readonly CollabPathSegment[]
  ): void {
    if (deepJsonEqual(prevVal, curVal)) {
      return;
    }
    if (isJsonObject(prevVal) && isJsonObject(curVal)) {
      diffObjectMembers(prevVal, curVal, nodeIdNorm, segments, null);
      return;
    }
    if (isJsonArray(prevVal) && isJsonArray(curVal)) {
      diffArray(prevVal, curVal, nodeIdNorm, segments);
      return;
    }
    emitPropSet(nodeIdNorm, segments, curVal);
  }

  function diffObjectMembers(
    prevObj: ICollabJsonObject,
    curObj: ICollabJsonObject,
    nodeIdNorm: string,
    baseSegments: readonly CollabPathSegment[],
    excludedKeys: ReadonlySet<string> | null
  ): void {
    for (const key of Object.keys(curObj)) {
      if (excludedKeys !== null && excludedKeys.has(key)) {
        continue;
      }
      const curVal: CollabJsonValue = curObj[key];
      if (curVal === undefined) {
        continue;
      }
      const segments: CollabPathSegment[] = [...baseSegments, { kind: "plain", key }];
      const prevVal: CollabJsonValue = prevObj[key];
      if (prevVal === undefined) {
        emitPropSet(nodeIdNorm, segments, curVal);
      } else {
        diffValue(prevVal, curVal, nodeIdNorm, segments);
      }
    }
    for (const key of Object.keys(prevObj)) {
      if (excludedKeys !== null && excludedKeys.has(key)) {
        continue;
      }
      if (prevObj[key] === undefined) {
        continue;
      }
      if (curObj[key] === undefined) {
        emitPropRemove(nodeIdNorm, [...baseSegments, { kind: "plain", key }]);
      }
    }
  }

  function diffArray(
    prevArr: readonly CollabJsonValue[],
    curArr: readonly CollabJsonValue[],
    nodeIdNorm: string,
    segments: readonly CollabPathSegment[]
  ): void {
    const keyKind: ArrayKeyKind | null = detectSharedArrayKey(prevArr, curArr);
    if (keyKind === null) {
      // Non-keyed or mixed arrays: whole-array replace on any difference.
      emitPropSet(nodeIdNorm, segments, curArr as CollabJsonValue[]);
      return;
    }
    const prevKeys: string[] = prevArr.map((element: CollabJsonValue): string => canonicalElementKey(element, keyKind));
    const curKeys: string[] = curArr.map((element: CollabJsonValue): string => canonicalElementKey(element, keyKind));
    if (hasDuplicates(prevKeys) || hasDuplicates(curKeys)) {
      emitPropSet(nodeIdNorm, segments, curArr as CollabJsonValue[]);
      return;
    }
    const prevKeySet: Set<string> = new Set<string>(prevKeys);
    const curKeySet: Set<string> = new Set<string>(curKeys);
    const commonPrevOrder: string[] = prevKeys.filter((key: string): boolean => curKeySet.has(key));
    const commonCurOrder: string[] = curKeys.filter((key: string): boolean => prevKeySet.has(key));
    // Granular keyed ops can reproduce `cur` exactly only when (a) the shared members
    // keep their relative order and (b) every new member sits at the TAIL of `cur`
    // (a final keyed selector that matches nothing APPENDS). Anything else — including
    // a pure reorder of the same key multiset — collapses to ONE whole-array propSet:
    // a deterministic coarse last-writer-wins that trades op granularity for exact
    // order convergence on every applier (TS and C# alike).
    const sameCommonOrder: boolean = stringArraysEqual(commonPrevOrder, commonCurOrder);
    const addedAtTail: boolean = stringArraysEqual(curKeys.slice(0, commonCurOrder.length), commonCurOrder);
    if (!sameCommonOrder || !addedAtTail) {
      emitPropSet(nodeIdNorm, segments, curArr as CollabJsonValue[]);
      return;
    }
    const prevByKey: Map<string, CollabJsonValue> = new Map<string, CollabJsonValue>();
    for (let i = 0; i < prevArr.length; i++) {
      prevByKey.set(prevKeys[i], prevArr[i]);
    }
    for (let i = 0; i < prevKeys.length; i++) {
      if (!curKeySet.has(prevKeys[i])) {
        emitPropRemove(nodeIdNorm, [
          ...segments,
          { kind: "selector", selectorKey: keyKind, selectorValue: prevKeys[i] }
        ]);
      }
    }
    for (let i = 0; i < commonCurOrder.length; i++) {
      const key: string = curKeys[i];
      const prevElement: CollabJsonValue = prevByKey.get(key);
      diffValue(prevElement, curArr[i], nodeIdNorm, [
        ...segments,
        { kind: "selector", selectorKey: keyKind, selectorValue: key }
      ]);
    }
    for (let i = commonCurOrder.length; i < curArr.length; i++) {
      emitPropSet(
        nodeIdNorm,
        [...segments, { kind: "selector", selectorKey: keyKind, selectorValue: curKeys[i] }],
        curArr[i]
      );
    }
  }

  function diffScreenFields(): void {
    const prevObj: ICollabJsonObject = prev as unknown as ICollabJsonObject;
    const curObj: ICollabJsonObject = cur as unknown as ICollabJsonObject;
    for (const key of Object.keys(curObj)) {
      if (EXCLUDED_SCREEN_KEYS.has(key)) {
        continue;
      }
      const curVal: CollabJsonValue = curObj[key];
      if (curVal === undefined) {
        continue;
      }
      if (deepJsonEqual(prevObj[key], curVal)) {
        continue;
      }
      ops.push({
        opId: makeOpId(),
        kind: "screenPropSet",
        path: buildPointer([{ kind: "plain", key }]),
        value: cloneJsonValue(curVal)
      });
    }
    for (const key of Object.keys(prevObj)) {
      if (EXCLUDED_SCREEN_KEYS.has(key)) {
        continue;
      }
      if (prevObj[key] === undefined) {
        continue;
      }
      if (curObj[key] === undefined) {
        ops.push({
          opId: makeOpId(),
          kind: "screenPropRemove",
          path: buildPointer([{ kind: "plain", key }])
        });
      }
    }
  }

  // 1. Structural adds + moves in cur document order.
  walkCurChildren("", curItems, false);

  // 2. Removes — topmost removed roots only, in prev document order. Survivors that
  //    escaped a doomed subtree were already moved out by step 1.
  for (const [idNorm, entry] of prevIndex) {
    if (curIndex.has(idNorm)) {
      continue;
    }
    const parentAlsoRemoved: boolean =
      entry.parentIdNorm !== "" && prevIndex.has(entry.parentIdNorm) && !curIndex.has(entry.parentIdNorm);
    if (parentAlsoRemoved) {
      continue;
    }
    ops.push({ opId: makeOpId(), kind: "nodeRemove", nodeId: idNorm });
  }

  // 3. Property diffs for surviving nodes, in cur document order.
  for (const [idNorm, curEntry] of curIndex) {
    const prevEntry: INodeIndexEntry | undefined = prevIndex.get(idNorm);
    if (prevEntry === undefined) {
      continue;
    }
    diffObjectMembers(
      prevEntry.node as unknown as ICollabJsonObject,
      curEntry.node as unknown as ICollabJsonObject,
      idNorm,
      [],
      EXCLUDED_NODE_KEYS
    );
  }

  // 4. Root screen fields.
  diffScreenFields();

  return ops;
}
